// src/pages/B2B/crm/api/meetingsClient.ts
import { useState, useEffect, useCallback } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { acquireApiToken } from '@/auth/acquireApiToken';
import {
  Meeting, MeetingListResponse, MeetingListFilters,
  MeetingCreatePayload, MeetingUpdatePayload,
} from './types';

const EMPTY: MeetingListResponse = { count: 0, meetings: [] };

async function authedFetch(
  instance: any,
  accounts: any[],
  apiUrl: string,
  path: string,
  init?: RequestInit,
): Promise<Response> {
  const tok = { accessToken: await acquireApiToken(instance, accounts[0]) };
  return fetch(`${apiUrl}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${tok.accessToken}`,
      'Content-Type': 'application/json',
      ...init?.headers,
    },
  });
}

export function useMeetingList(apiUrl: string, filters: MeetingListFilters = {}) {
  const isAuthed = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const [data, setData] = useState<MeetingListResponse>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trigger, setTrigger] = useState(0);
  const refetch = useCallback(() => setTrigger(t => t + 1), []);

  const filtersKey = JSON.stringify(filters);

  useEffect(() => {
    if (!isAuthed || accounts.length === 0) return;
    let mounted = true;
    (async () => {
      setLoading(true); setError(null);
      try {
        const qs = new URLSearchParams();
        if (filters.from) qs.set('from', filters.from);
        if (filters.to) qs.set('to', filters.to);
        if (filters.venue_id) qs.set('venue_id', filters.venue_id);
        const path = `/admin/b2b/crm/meetings${qs.toString() ? `?${qs}` : ''}`;
        const res = await authedFetch(instance, accounts, apiUrl, path);
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
        if (mounted) setData(json);
      } catch (e: any) {
        if (mounted) { setError(e?.message ?? 'Failed to load meetings'); setData(EMPTY); }
      } finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed, instance, accounts, apiUrl, filtersKey, trigger]);

  return { data, loading, error, refetch };
}

export function useMeetingMutations(apiUrl: string) {
  const { instance, accounts } = useMsal();

  async function createMeeting(payload: MeetingCreatePayload): Promise<{ id: string }> {
    const res = await authedFetch(instance, accounts, apiUrl, `/admin/b2b/crm/meetings`, {
      method: 'POST', body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error((await res.json()).error || `HTTP ${res.status}`);
    return res.json();
  }

  async function updateMeeting(id: string, payload: MeetingUpdatePayload) {
    const res = await authedFetch(instance, accounts, apiUrl, `/admin/b2b/crm/meetings/${id}`, {
      method: 'PATCH', body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error((await res.json()).error || `HTTP ${res.status}`);
    return res.json();
  }

  async function deleteMeeting(id: string) {
    const res = await authedFetch(instance, accounts, apiUrl, `/admin/b2b/crm/meetings/${id}`, {
      method: 'DELETE',
    });
    if (!res.ok) throw new Error((await res.json()).error || `HTTP ${res.status}`);
    return res.json();
  }

  return { createMeeting, updateMeeting, deleteMeeting };
}
